import { resolveMediaUrl } from '@/lib/media';
import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Star, ExternalLink, Check, MessageSquare, Send, Sparkles, Copy, RefreshCw, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const DEFAULT_ASPECTS = [
  'Friendly service', 'Quick & efficient', 'Very knowledgeable',
  'Made me feel welcome', 'Went above and beyond', 'Great atmosphere',
  'Highly professional', 'Excellent attention to detail', 'Patient & helpful',
  'Best experience ever',
];

const ReviewPage = () => {
  const { username, staffId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [rating, setRating] = useState(0);
  const [hoveredStar, setHoveredStar] = useState(0);
  // steps: 'rate' | 'feedback' | 'generate' | 'review' | 'done'
  const [step, setStep] = useState('rate');
  const [submitting, setSubmitting] = useState(false);
  const [feedback, setFeedback] = useState('');
  const [reviewerName, setReviewerName] = useState('');
  const [selectedAspects, setSelectedAspects] = useState([]);
  const [generatedReviews, setGeneratedReviews] = useState([]);
  const [selectedReviewIdx, setSelectedReviewIdx] = useState(0);
  const [generating, setGenerating] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await axios.get(`${API}/api/review/${username}/${staffId}`);
        setData(res.data);
      } catch { setNotFound(true); }
      finally { setLoading(false); }
    };
    fetchData();
  }, [username, staffId]);

  const handleSubmitRating = async () => {
    if (!rating) return;
    if (rating <= 3) {
      setStep('feedback');
      return;
    }
    // 4-5 stars: save rating then show AI generator
    setSubmitting(true);
    try {
      await axios.post(`${API}/api/review/${staffId}/rate`, { rating });
    } catch { /* continue anyway */ }
    finally { setSubmitting(false); }
    setStep('generate');
  };

  const handleSubmitFeedback = async () => {
    setSubmitting(true);
    try {
      await axios.post(`${API}/api/review/${staffId}/rate`, {
        rating, feedback: feedback.trim(), reviewer_name: reviewerName.trim(),
      });
      setStep('done');
    } catch { setStep('done'); }
    finally { setSubmitting(false); }
  };

  const handleSubmitFeedbackSkip = async () => {
    setSubmitting(true);
    try {
      await axios.post(`${API}/api/review/${staffId}/rate`, { rating });
      setStep('done');
    } catch { setStep('done'); }
    finally { setSubmitting(false); }
  };

  const toggleAspect = (a) => {
    setSelectedAspects(prev =>
      prev.includes(a) ? prev.filter(x => x !== a) : [...prev, a]
    );
  };

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      const res = await axios.post(`${API}/api/review/${staffId}/generate`, {
        aspects: selectedAspects.length ? selectedAspects : ['Great service'],
        rating,
      });
      const reviews = res.data.reviews || [res.data.review].filter(Boolean);
      setGeneratedReviews(reviews);
      setSelectedReviewIdx(0);
      setStep('review');
    } catch {
      setGeneratedReviews([`Had a wonderful experience! ${data?.staff_name || 'The staff'} was fantastic. Highly recommend!`]);
      setSelectedReviewIdx(0);
      setStep('review');
    }
    finally { setGenerating(false); }
  };

  const handleCopyAndGo = (e) => {
    const reviewText = generatedReviews[selectedReviewIdx] || '';
    try {
      navigator.clipboard.writeText(reviewText);
    } catch { /* clipboard may fail silently on some browsers */ }
    setCopied(true);
    setTimeout(() => setStep('done'), 800);
  };

  if (loading) return (
    <div className="min-h-screen bg-[#050505] flex items-center justify-center">
      <div className="w-8 h-8 border-2 border-violet-500 border-t-transparent rounded-full animate-spin" />
    </div>
  );

  if (notFound) return (
    <div className="min-h-screen bg-[#050505] flex items-center justify-center text-center p-4">
      <div>
        <h1 className="text-2xl font-bold text-white mb-2">Not found</h1>
        <p className="text-[#71717A]">This review link is invalid</p>
      </div>
    </div>
  );

  const accent = data.theme_color || '#8B5CF6';

  return (
    <div className="min-h-screen bg-[#050505] flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <AnimatePresence mode="wait">

          {/* ══════ STEP 1: STAR RATING ══════ */}
          {step === 'rate' && (
            <motion.div key="rate" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }}
              className="bg-[#121212] rounded-2xl border border-[#27272A] overflow-hidden" data-testid="review-card">
              <div className="h-1.5" style={{ background: accent }} />
              <div className="p-6 text-center space-y-5">
                {data.staff_photo ? (
                  <img src={resolveMediaUrl(data.staff_photo)} alt={data.staff_name}
                    className="w-20 h-20 rounded-full object-cover mx-auto border-2" style={{ borderColor: accent }} />
                ) : (
                  <div className="w-20 h-20 rounded-full mx-auto flex items-center justify-center text-2xl font-bold"
                    style={{ backgroundColor: `${accent}20`, color: accent }}>
                    {data.staff_name.charAt(0)}
                  </div>
                )}
                <div>
                  <h1 className="text-xl font-bold text-white" data-testid="staff-name">{data.staff_name}</h1>
                  {data.staff_role && <p className="text-[#A1A1AA] text-sm">{data.staff_role}</p>}
                  {data.business_name && <p className="text-sm mt-1" style={{ color: accent }}>{data.business_name}</p>}
                </div>
                <div>
                  <p className="text-[#A1A1AA] text-sm mb-3">How was your experience?</p>
                  <div className="flex items-center justify-center gap-2" data-testid="star-rating">
                    {[1, 2, 3, 4, 5].map(star => (
                      <button key={star} data-testid={`star-${star}`}
                        onMouseEnter={() => setHoveredStar(star)}
                        onMouseLeave={() => setHoveredStar(0)}
                        onClick={() => setRating(star)}
                        className="transition-transform hover:scale-110">
                        <Star className={`w-10 h-10 transition-colors ${
                          star <= (hoveredStar || rating) ? 'text-amber-400 fill-amber-400' : 'text-[#27272A]'
                        }`} />
                      </button>
                    ))}
                  </div>
                  {rating > 0 && (
                    <p className="text-xs mt-2" style={{ color: accent }}>
                      {rating === 5 ? 'Excellent!' : rating === 4 ? 'Great!' : rating === 3 ? 'Good' : rating === 2 ? 'Fair' : 'Poor'}
                    </p>
                  )}
                </div>
                <Button data-testid="submit-rating" onClick={handleSubmitRating} disabled={!rating || submitting}
                  className="w-full rounded-xl h-12 font-semibold text-base"
                  style={{ backgroundColor: accent, color: '#fff', opacity: !rating ? 0.5 : 1 }}>
                  {submitting ? 'Submitting...' : 'Submit Rating'}
                </Button>
              </div>
            </motion.div>
          )}

          {/* ══════ STEP 2a: LOW RATING → PRIVATE FEEDBACK ══════ */}
          {step === 'feedback' && (
            <motion.div key="feedback" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }}
              className="bg-[#121212] rounded-2xl border border-[#27272A] overflow-hidden" data-testid="feedback-card">
              <div className="h-1.5" style={{ background: accent }} />
              <div className="p-6 space-y-5">
                <div className="text-center">
                  <div className="w-14 h-14 rounded-full mx-auto flex items-center justify-center mb-3"
                    style={{ backgroundColor: `${accent}15` }}>
                    <MessageSquare className="w-7 h-7" style={{ color: accent }} />
                  </div>
                  <h2 className="text-lg font-bold text-white">We'd love your feedback</h2>
                  <p className="text-[#A1A1AA] text-sm mt-1">Help us improve your experience</p>
                  <div className="flex items-center justify-center gap-1 mt-2">
                    {[1, 2, 3, 4, 5].map(s => (
                      <Star key={s} className={`w-5 h-5 ${s <= rating ? 'text-amber-400 fill-amber-400' : 'text-[#27272A]'}`} />
                    ))}
                  </div>
                </div>
                <div>
                  <label className="text-[#A1A1AA] text-xs block mb-1.5">Your name (optional)</label>
                  <input data-testid="reviewer-name" value={reviewerName} onChange={e => setReviewerName(e.target.value)}
                    className="w-full bg-[#1E1E1E] border border-[#27272A] rounded-xl px-4 py-2.5 text-white text-sm placeholder-[#525252] focus:outline-none focus:border-violet-500/50"
                    placeholder="Enter your name" />
                </div>
                <div>
                  <label className="text-[#A1A1AA] text-xs block mb-1.5">What could we do better?</label>
                  <textarea data-testid="feedback-text" value={feedback} onChange={e => setFeedback(e.target.value)}
                    rows={4}
                    className="w-full bg-[#1E1E1E] border border-[#27272A] rounded-xl px-4 py-3 text-white text-sm placeholder-[#525252] resize-none focus:outline-none focus:border-violet-500/50"
                    placeholder="Tell us about your experience..." />
                </div>
                <div className="flex gap-3">
                  <button data-testid="skip-feedback" onClick={handleSubmitFeedbackSkip}
                    className="flex-1 py-3 rounded-xl border border-[#27272A] text-[#A1A1AA] text-sm font-medium hover:bg-white/5 transition-colors">
                    Skip
                  </button>
                  <Button data-testid="submit-feedback" onClick={handleSubmitFeedback} disabled={submitting}
                    className="flex-1 rounded-xl h-12 font-semibold"
                    style={{ backgroundColor: accent, color: '#fff' }}>
                    {submitting ? 'Sending...' : <><Send className="w-4 h-4 mr-2" /> Send Feedback</>}
                  </Button>
                </div>
              </div>
            </motion.div>
          )}

          {/* ══════ STEP 2b: HIGH RATING → PICK ASPECTS ══════ */}
          {step === 'generate' && (
            <motion.div key="generate" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }}
              className="bg-[#121212] rounded-2xl border border-[#27272A] overflow-hidden" data-testid="generate-card">
              <div className="h-1.5" style={{ background: accent }} />
              <div className="p-6 space-y-5">
                <div className="text-center">
                  <div className="w-14 h-14 rounded-full mx-auto flex items-center justify-center mb-3"
                    style={{ backgroundColor: `${accent}15` }}>
                    <Sparkles className="w-7 h-7" style={{ color: accent }} />
                  </div>
                  <h2 className="text-lg font-bold text-white">Help us on Google!</h2>
                  <p className="text-[#A1A1AA] text-sm mt-1">
                    Pick what you liked about <strong className="text-white">{data.staff_name}</strong>
                  </p>
                  <div className="flex items-center justify-center gap-1 mt-2">
                    {[1, 2, 3, 4, 5].map(s => (
                      <Star key={s} className={`w-5 h-5 ${s <= rating ? 'text-amber-400 fill-amber-400' : 'text-[#27272A]'}`} />
                    ))}
                  </div>
                </div>
                <div className="flex flex-wrap gap-2 justify-center" data-testid="aspect-picker">
                  {(data.review_categories?.length > 0 ? data.review_categories : DEFAULT_ASPECTS).map(a => (
                    <button key={a} data-testid={`aspect-${a.replace(/\s+/g, '-').toLowerCase()}`}
                      onClick={() => toggleAspect(a)}
                      className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${
                        selectedAspects.includes(a)
                          ? 'text-white border-transparent'
                          : 'text-[#A1A1AA] border-[#27272A] hover:border-[#525252]'
                      }`}
                      style={selectedAspects.includes(a) ? { backgroundColor: accent, borderColor: accent } : {}}>
                      {a}
                    </button>
                  ))}
                </div>
                {selectedAspects.length > 0 && (
                  <p className="text-[#525252] text-[10px] text-center">{selectedAspects.length} selected</p>
                )}
                <Button data-testid="generate-review-btn" onClick={handleGenerate} disabled={generating}
                  className="w-full rounded-xl h-12 font-semibold text-base"
                  style={{ backgroundColor: accent, color: '#fff' }}>
                  {generating ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Writing your review...</>
                  ) : (
                    <><Sparkles className="w-4 h-4 mr-2" /> Generate Review</>
                  )}
                </Button>
                {data?.google_review_link ? (
                  <a href={data.google_review_link} target="_blank" rel="noopener noreferrer"
                    data-testid="skip-generate" onClick={() => setTimeout(() => setStep('done'), 100)}
                    className="block w-full text-center text-[#525252] text-xs hover:text-[#71717A] transition-colors">
                    Skip, I'll write my own
                  </a>
                ) : (
                  <button data-testid="skip-generate" onClick={() => setStep('done')}
                    className="w-full text-center text-[#525252] text-xs hover:text-[#71717A] transition-colors">
                    Skip, I'll write my own
                  </button>
                )}
              </div>
            </motion.div>
          )}

          {/* ══════ STEP 3: SHOW GENERATED REVIEWS → PICK & COPY ══════ */}
          {step === 'review' && (
            <motion.div key="review" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }}
              className="bg-[#121212] rounded-2xl border border-[#27272A] overflow-hidden" data-testid="review-result-card">
              <div className="h-1.5" style={{ background: accent }} />
              <div className="p-6 space-y-5">
                <div className="text-center">
                  <div className="w-14 h-14 rounded-full mx-auto flex items-center justify-center mb-3"
                    style={{ backgroundColor: `${accent}15` }}>
                    <Check className="w-7 h-7" style={{ color: accent }} />
                  </div>
                  <h2 className="text-lg font-bold text-white">Pick your favorite review!</h2>
                  <p className="text-[#A1A1AA] text-sm mt-1">Tap to select, then copy & paste on Google</p>
                </div>
                <div className="space-y-2.5 max-h-64 overflow-y-auto">
                  {generatedReviews.map((review, idx) => (
                    <button key={idx} onClick={() => setSelectedReviewIdx(idx)}
                      data-testid={`review-option-${idx}`}
                      className={`w-full text-left p-3.5 rounded-xl border transition-all ${
                        selectedReviewIdx === idx
                          ? 'border-2 bg-[#1A1A2E]'
                          : 'border-[#27272A] bg-[#1E1E1E] hover:border-[#3F3F46]'
                      }`}
                      style={selectedReviewIdx === idx ? { borderColor: accent } : {}}>
                      <p className="text-white text-sm leading-relaxed">{review}</p>
                    </button>
                  ))}
                </div>
                <div className="flex gap-3">
                  <button data-testid="regenerate-btn" onClick={() => { setStep('generate'); }}
                    className="p-3 rounded-xl border border-[#27272A] text-[#A1A1AA] hover:bg-white/5 transition-colors"
                    title="Generate new reviews">
                    <RefreshCw className="w-5 h-5" />
                  </button>
                  {data?.google_review_link ? (
                    <a href={data.google_review_link} target="_blank" rel="noopener noreferrer"
                      data-testid="copy-and-go-btn" onClick={handleCopyAndGo}
                      className="flex-1 flex items-center justify-center rounded-xl h-12 font-semibold text-base text-white no-underline"
                      style={{ backgroundColor: accent }}>
                      {copied ? (
                        <><Check className="w-4 h-4 mr-2" /> Copied! Opening Google...</>
                      ) : (
                        <><Copy className="w-4 h-4 mr-2" /> Copy & Go to Google</>
                      )}
                    </a>
                  ) : (
                    <Button data-testid="copy-and-go-btn" onClick={handleCopyAndGo}
                      className="flex-1 rounded-xl h-12 font-semibold text-base"
                      style={{ backgroundColor: accent, color: '#fff' }}>
                      {copied ? (
                        <><Check className="w-4 h-4 mr-2" /> Copied!</>
                      ) : (
                        <><Copy className="w-4 h-4 mr-2" /> Copy Review</>
                      )}
                    </Button>
                  )}
                </div>
              </div>
            </motion.div>
          )}

          {/* ══════ STEP 4: THANK YOU ══════ */}
          {step === 'done' && (
            <motion.div key="done" initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
              className="bg-[#121212] rounded-2xl border border-[#27272A] overflow-hidden" data-testid="review-thanks">
              <div className="h-1.5" style={{ background: accent }} />
              <div className="p-6 text-center space-y-5">
                <div className="w-16 h-16 rounded-full mx-auto flex items-center justify-center"
                  style={{ backgroundColor: `${accent}20` }}>
                  <Check className="w-8 h-8" style={{ color: accent }} />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-white">Thank you!</h2>
                  <p className="text-[#A1A1AA] text-sm mt-1">
                    Your feedback for <strong className="text-white">{data.staff_name}</strong> has been recorded.
                  </p>
                </div>
                <div className="flex items-center justify-center gap-1">
                  {[1, 2, 3, 4, 5].map(s => (
                    <Star key={s} className={`w-6 h-6 ${s <= rating ? 'text-amber-400 fill-amber-400' : 'text-[#27272A]'}`} />
                  ))}
                </div>
                {rating <= 3 && (
                  <p className="text-[#525252] text-xs">Your feedback helps us serve you better.</p>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
        <p className="text-center mt-6 text-[#525252] text-xs">
          Powered by <a href="/" className="hover:text-[#71717A]">One Link</a>
        </p>
      </div>
    </div>
  );
};

export default ReviewPage;
